/* eslint-disable typescript-sort-keys/interface */
/* eslint-disable sort-keys */
import { designTokens } from './tokens';
import { makeComplexStyles, makeCustomNestedStyle, makeStyles, StyleConfig } from './utils/styleProps';
import { CSSProperties } from 'react';
import ColorsTheme from '../types/theme/color';
import Theme from '../types/theme';

export type PaletteColor = typeof designTokens.colorPalette;
export type ThemeColor = Omit<ColorsTheme, 'statistic'>;

type AllColors = PaletteColor & ThemeColor;

export type Color = {
  [K in keyof AllColors]: string;
};

// WARNING: Never change the declaration order
export const breakpointSelectorMap = {
  is2XL: designTokens.mediaQueries['2xl'],
  isLG: designTokens.mediaQueries.lg,
  isMD: designTokens.mediaQueries.md,
  isSM: designTokens.mediaQueries.sm,
  isXL: designTokens.mediaQueries.xl,
};

// WARNING: Never change the declaration order
export const pseudoSelectorMap = {
  isActive: 'active',
  isChecked: 'checked',
  isDisabled: 'disabled',
  isEmpty: 'empty',
  isEnabled: 'enabled',
  isFirstChild: 'first-child',
  isFirstOfType: 'first-of-type',
  isFocus: 'focus',
  isFullscreen: 'fullscreen',
  isHover: 'hover',
  isInvalid: 'invalid',
  isLastChild: 'last-child',
  isLastOfType: 'last-of-type',
  isLink: 'link',
  isOnlyChild: 'only-child',
  isOnlyOfType: 'only-of-type',
  isOptional: 'optional',
  isOutOfRange: 'out-of-range',
  isReadWrite: 'read-write',
  isRequired: 'required',
  isRoot: 'root',
  isTarget: 'target',
  isValid: 'valid',
  isVisited: 'visited',
};

export const colorProps = [
  'bg',
  'borderBottomColor',
  'borderColor',
  'borderLeftColor',
  'borderRightColor',
  'borderTopColor',
  'borderXColor',
  'borderYColor',
  'color',
  'divideXColor',
  'divideYColor',
  'fill',
  'placeholderColor',
  'stroke',
] as (keyof StyleColorProps)[];

const getColorProps = (themeColors?: ThemeColor): StyleColorProps => {
  const colors = {
    blendMode: 'none',
    ...designTokens.colorPalette,
    ...(themeColors || {}),
  } as Color;

  return {
    /**
     * Background Color
     */
    bg: makeStyles<keyof CSSProperties, Color>('backgroundColor', colors),
    borderBottomColor: makeStyles<keyof CSSProperties, Color>('borderBottomColor', colors),
    borderColor: makeStyles<keyof CSSProperties, Color>('borderColor', colors),
    borderLeftColor: makeStyles<keyof CSSProperties, Color>('borderLeftColor', colors),
    borderRightColor: makeStyles<keyof CSSProperties, Color>('borderRightColor', colors),
    borderTopColor: makeStyles<keyof CSSProperties, Color>('borderTopColor', colors),
    borderXColor: makeComplexStyles<keyof CSSProperties, Color>(
      ['borderLeftColor', 'borderRightColor'],
      colors as Color,
    ),
    borderYColor: makeComplexStyles<keyof CSSProperties, Color>(
      ['borderTopColor', 'borderBottomColor'],
      colors as Color,
    ),

    color: makeStyles<keyof CSSProperties, Color>('color', colors),

    /**
     * Horizontal divider color
     */
    divideXColor: makeCustomNestedStyle<keyof CSSProperties, Color>(['borderLeftColor'], '> * + *', colors),

    /**
     * Vertical divider color
     */
    divideYColor: makeCustomNestedStyle<keyof CSSProperties, Color>(['borderTopColor'], '> * + *', colors),

    /**
     * SVG Fill Color
     */
    fill: makeStyles<keyof CSSProperties, Color>('fill', colors),

    placeholderColor: makeCustomNestedStyle<keyof CSSProperties, Color>(['color'], '&::placeholder', colors),

    /**
     * SVG Stroke Color
     */
    stroke: makeStyles<keyof CSSProperties, Color>('stroke', colors),
  };
};

export const propsWithOptions = {
  alignContent: makeStyles('alignContent', designTokens.alignContent),
  alignItems: makeStyles('alignItems', designTokens.alignItems),
  alignSelf: makeStyles('alignSelf', designTokens.alignSelf),

  bgAttach: makeStyles('backgroundAttachment', designTokens.backgroundAttachment),
  bgBlend: makeStyles('backgroundBlendMode', designTokens.blendMode),
  bgClip: makeStyles('backgroundClip', designTokens.backgroundClip),
  bgPosition: makeStyles('backgroundPosition', designTokens.backgroundPosition),
  bgRepeat: makeStyles('backgroundRepeat', designTokens.backgroundRepeat),
  bgSize: makeStyles('backgroundSize', designTokens.backgroundSize),
  borderBottomStyle: makeStyles('borderBottomStyle', designTokens.borderStyle),
  borderBottomWidth: makeStyles('borderBottomWidth', designTokens.borderWidth),
  borderLeftStyle: makeStyles('borderLeftStyle', designTokens.borderStyle),
  borderLeftWidth: makeStyles('borderLeftWidth', designTokens.borderWidth),
  borderRightStyle: makeStyles('borderRightStyle', designTokens.borderStyle),
  borderRightWidth: makeStyles('borderRightWidth', designTokens.borderWidth),
  borderStyle: makeStyles('borderStyle', designTokens.borderStyle),
  borderTopStyle: makeStyles('borderTopStyle', designTokens.borderStyle),
  borderTopWidth: makeStyles('borderTopWidth', designTokens.borderWidth),
  borderWidth: makeStyles('borderWidth', designTokens.borderWidth),
  borderXStyle: makeComplexStyles(['borderLeftStyle', 'borderRightStyle'], designTokens.borderStyle),
  borderXWidth: makeComplexStyles(['borderLeftWidth', 'borderRightWidth'], designTokens.borderWidth),
  borderYStyle: makeComplexStyles(['borderTopStyle', 'borderBottomStyle'], designTokens.borderStyle),
  borderYWidth: makeComplexStyles(['borderTopWidth', 'borderBottomWidth'], designTokens.borderWidth),
  bottom: makeStyles('bottom', {
    ...designTokens.spaceWithAuto,
    screen: '100vh',
  }),
  boxSizing: makeStyles('boxSizing', designTokens.boxSizing),
  cursor: makeStyles('cursor', designTokens.cursor),
  display: makeStyles('display', designTokens.display),
  /**
   * Horizontal divider style
   */
  divideXStyle: makeCustomNestedStyle(['borderLeftStyle'], '> * + *', designTokens.borderStyle),

  /**
   * Horizontal divider width
   */
  divideXWidth: makeCustomNestedStyle(['borderLeftWidth'], '> * + *', designTokens.borderWidth),

  /**
   * Vertical divider style
   */
  divideYStyle: makeCustomNestedStyle(['borderTopStyle'], '> * + *', designTokens.borderStyle),

  /**
   * Vertical divider width
   */
  divideYWidth: makeCustomNestedStyle(['borderTopWidth'], '> * + *', designTokens.borderWidth),

  flex: makeStyles('flex', designTokens.flex),

  flexDir: makeStyles('flexDirection', designTokens.flexDirection),

  flexGrow: makeStyles('flexGrow', designTokens.flexGrow),

  flexShrink: makeStyles('flexShrink', designTokens.flexShrink),

  flexWrap: makeStyles('flexWrap', designTokens.flexWrap),

  float: makeStyles('display', designTokens.float),

  font: makeStyles('fontFamily', designTokens.font),

  fontSize: makeStyles('fontSize', designTokens.fontSize),

  fontWeight: makeStyles('fontWeight', designTokens.fontWeight),

  /**
   * Grid Gap
   */
  gap: makeStyles('gap', designTokens.space),

  /**
   * Grid Gap Horizontal
   */
  gapX: makeStyles('columnGap', designTokens.space),

  /**
   * Grid Gap Vertical
   */
  gapY: makeStyles('rowGap', designTokens.space),

  gridAutoCols: makeStyles('gridAutoColumns', designTokens.gridAuto),
  gridAutoRows: makeStyles('gridAutoRows', designTokens.gridAuto),
  gridColEnd: makeStyles('gridColumnEnd', designTokens.gridEnd),
  gridColSpan: makeStyles('gridColumn', designTokens.gridSpan),
  gridColStart: makeStyles('gridColumnStart', designTokens.gridStart),
  gridCols: makeStyles('gridTemplateColumns', designTokens.gridTemplate),
  gridFlow: makeStyles('gridAutoFlow', designTokens.gridAutoFlow),
  gridRowEnd: makeStyles('gridRowEnd', designTokens.gridEnd),
  gridRowSpan: makeStyles('gridRow', designTokens.gridSpan),
  gridRowStart: makeStyles('gridRowStart', designTokens.gridStart),
  gridRows: makeStyles('gridTemplateRows', designTokens.gridTemplate),
  height: makeStyles('height', {
    ...designTokens.spaceWithAuto,
    screen: '100vh',
  }),
  justifyContent: makeStyles('justifyContent', designTokens.justifyContent),
  justifyItems: makeStyles('justifyItems', designTokens.justifyItems),
  justifySelf: makeStyles('justifySelf', designTokens.justifySelf),
  leading: makeStyles('lineHeight', designTokens.lineHeight),
  left: makeStyles('left', {
    ...designTokens.spaceWithAuto,
    screen: '100vw',
  }),
  listStyle: makeStyles('listStyleType', designTokens.listStyle),
  listStylePosition: makeStyles('listStylePosition', designTokens.listStylePosition),

  /**
   * Margin
   */
  m: makeStyles('margin', designTokens.spaceWithAuto),

  /**
   * Max Height
   */
  maxH: makeStyles('maxHeight', {
    ...designTokens.spaceWithNone,
    screen: '100vh',
  }),

  /**
   * Max Width
   */
  maxW: makeStyles('maxWidth', {
    ...designTokens.spaceWithNone,
    screen: '100vw',
  }),

  /**
   * Margin Bottom
   */
  mb: makeStyles('marginBottom', {
    ...designTokens.spaceWithAuto,
    screen: '100vh',
  }),

  /**
   * Min Height
   */
  minH: makeStyles(
    'minHeight',
    makeStyles('height', {
      ...designTokens.spaceWithNone,
      screen: '100vh',
    }),
  ),

  /**
   * Min Width
   */
  minW: makeStyles(
    'minWidth',
    makeStyles('width', {
      ...designTokens.spaceWithNone,
      screen: '100vw',
    }),
  ),

  mixBlend: makeStyles('mixBlendMode', designTokens.blendMode),

  /**
   * Margin Left
   */
  ml: makeStyles('marginLeft', {
    ...designTokens.spaceWithAuto,
    screen: '100vw',
  }),

  /**
   * Margin Right
   */
  mr: makeStyles('marginRight', {
    ...designTokens.spaceWithAuto,
    screen: '100vw',
  }),

  /**
   * Margin Top
   */
  mt: makeStyles('marginTop', {
    ...designTokens.spaceWithAuto,
    screen: '100vh',
  }),

  /**
   * Margin Horizontal
   */
  mx: makeComplexStyles(['marginLeft', 'marginRight'], {
    ...designTokens.spaceWithAuto,
    screen: '100vw',
  }),

  /**
   * Margin Vertical
   */
  my: makeComplexStyles(['marginTop', 'marginBottom'], {
    ...designTokens.spaceWithAuto,
    screen: '100vh',
  }),

  opacity: makeStyles('opacity', designTokens.opacity),

  /**
   * Flex Order
   */
  order: makeStyles('order', designTokens.flexOrder),

  overflow: makeStyles('overflow', designTokens.overflow),
  overflowX: makeStyles('overflowX', designTokens.overflow),
  overflowY: makeStyles('overflowY', designTokens.overflow),

  /**
   * Padding
   */
  p: makeStyles('padding', designTokens.space),

  /**
   * Padding Bottom
   */
  pb: makeStyles('paddingBottom', designTokens.space),

  /**
   * Padding Left
   */
  pl: makeStyles('paddingLeft', designTokens.space),

  placeContent: makeStyles('placeContent', designTokens.placeContent),
  placeItems: makeStyles('placeItems', designTokens.placeItems),
  placeSelf: makeStyles('placeSelf', designTokens.placeSelf),

  pointerEvents: makeStyles('pointerEvents', designTokens.pointerEvents),
  position: makeStyles('position', designTokens.position),

  /**
   * Padding Right
   */
  pr: makeStyles('paddingRight', designTokens.space),

  /**
   * Padding Top
   */
  pt: makeStyles('paddingTop', designTokens.space),

  /**
   * Padding Horizontal
   */
  px: makeComplexStyles(['paddingLeft', 'paddingRight'], designTokens.space),

  /**
   * Padding Vertical
   */
  py: makeComplexStyles(['paddingTop', 'paddingBottom'], designTokens.space),

  resize: makeStyles('resize', designTokens.resize),
  right: makeStyles('right', {
    ...designTokens.spaceWithAuto,
    screen: '100vw',
  }),

  /**
   * Border Radius
   */
  rounded: makeStyles('borderRadius', designTokens.borderRadius),

  /**
   * Border Radius Bottom
   */
  roundedB: makeComplexStyles(['borderBottomLeftRadius', 'borderBottomRightRadius'], designTokens.borderRadius),

  /**
   * Border Radius Bottom Left
   */
  roundedBL: makeStyles('borderBottomLeftRadius', designTokens.borderRadius),

  /**
   * Border Radius Bottom Right
   */
  roundedBR: makeStyles('borderBottomRightRadius', designTokens.borderRadius),

  /**
   * Border Radius Bottom Left
   */
  roundedL: makeComplexStyles(['borderBottomLeftRadius', 'borderTopLeftRadius'], designTokens.borderRadius),

  /**
   * Border Radius Bottom Right
   */
  roundedR: makeComplexStyles(['borderTopRightRadius', 'borderBottomRightRadius'], designTokens.borderRadius),

  /**
   * Border Radius Bottom Top
   */
  roundedT: makeComplexStyles(['borderTopLeftRadius', 'borderTopRightRadius'], designTokens.borderRadius),

  /**
   * Border Radius Bottom
   */
  roundedTL: makeStyles('borderTopLeftRadius', designTokens.borderRadius),

  /**
   * Border Radius Bottom Top Right
   */
  roundedTR: makeStyles('borderTopRightRadius', designTokens.borderRadius),

  /**
   * Box Shadow
   */
  shadow: makeStyles('boxShadow', designTokens.shadow), // TODO: Make it themeable

  /**
   * Horizontal space between children elements using margin-left.
   */
  spaceX: makeCustomNestedStyle(['marginLeft'], '> * + *', designTokens.space),

  /**
   * Vertical space between children elements using margin-top.
   */
  spaceY: makeCustomNestedStyle(['marginTop'], '> * + *', designTokens.space),

  /**
   * SVG Stroke Width
   */
  strokeWidth: makeStyles('strokeWidth', designTokens.strokeWidth),

  tableLayout: makeStyles('tableLayout', designTokens.tableLayout),
  textAlign: makeStyles('textAlign', designTokens.textAlign),
  textDecoration: makeStyles('textDecoration', designTokens.textDecoration),
  textOverflow: makeStyles('textOverflow', designTokens.textOverflow),
  top: makeStyles('top', {
    ...designTokens.spaceWithAuto,
    screen: '100vh',
  }),
  tracking: makeStyles('letterSpacing', designTokens.letterSpacing),
  userSelect: makeStyles('userSelect', designTokens.userSelect),
  verticalAlign: makeStyles('verticalAlign', designTokens.verticalAlign),
  whiteSpace: makeStyles('whiteSpace', designTokens.whiteSpace),
  width: makeStyles('width', {
    ...designTokens.spaceWithAuto,
    screen: '100vw',
  }),
  zIndex: makeStyles('zIndex', designTokens.zIndex),
};

// WARNING: Never change the declaration order
export const validProps = [
  ...colorProps,
  ...Object.keys(propsWithOptions),
  ...Object.keys(designTokens.booleanProps),
  ...Object.keys(pseudoSelectorMap),
  ...Object.keys(breakpointSelectorMap),
];

const styleProps = {
  ...propsWithOptions,
  ...designTokens.booleanProps,
};

export function getPropStyles<T extends keyof StyleProps | keyof StyleColorProps, K extends keyof StyleProps[T]>(
  prop: T,
  variant?: K,
  theme?: Theme,
): StyleProps[T][K] | StyleProps[T] | undefined {
  if (!(prop in styleProps) && !colorProps.includes(prop as keyof StyleColorProps)) {
    throw new Error(`Design Tokens: There is no token called "${prop}"`);
  }

  let property;

  if (colorProps.includes(prop as keyof StyleColorProps)) {
    const colorProps = getColorProps(theme?.colors);
    property = colorProps[prop as keyof StyleColorProps];
  } else {
    property = styleProps[prop as keyof typeof styleProps];
  }

  if (variant) {
    if (!(variant in property)) {
      throw new Error(`Design Tokens: There is no variant called "${variant as string}" for "${prop}"`);
    }

    return property[variant as keyof typeof property];
  } else if (prop in designTokens.booleanProps) {
    return property as StyleProps[T];
  }

  return undefined;
}

export type StyleColorProps = {
  bg: StyleConfig<Color>;
  borderBottomColor: StyleConfig<Color>;
  borderColor: StyleConfig<Color>;
  borderLeftColor: StyleConfig<Color>;
  borderRightColor: StyleConfig<Color>;
  borderTopColor: StyleConfig<Color>;
  borderXColor: StyleConfig<Color>;
  borderYColor: StyleConfig<Color>;
  divideXColor: StyleConfig<Color>;
  divideYColor: StyleConfig<Color>;
  color: StyleConfig<Color>;
  fill: StyleConfig<Color>;
  placeholderColor: StyleConfig<Color>;
  stroke: StyleConfig<Color>;
};

export type StyleProps = typeof styleProps & StyleColorProps;
export type StylePropsWithOptions = typeof propsWithOptions;
export type StyleBooleanProps = typeof designTokens.booleanProps;
